import { BluetoothServiceCallbacks } from '../types';

class UsbSerialService implements BluetoothServiceCallbacks {
  private port: SerialPort | null = null;
  private reader: ReadableStreamDefaultReader<Uint8Array> | null = null;
  private writer: WritableStreamDefaultWriter<Uint8Array> | null = null;
  private _receivedData: string = '';
  private _isReading: boolean = false;
  
  public onDisconnected?: () => void;
  public onDataReceived?: (data: string) => void;

  // Check if Web Serial is supported by the browser
  isSupported(): boolean {
    return 'serial' in navigator;
  }

  // Request USB serial port and connect
  async connect(): Promise<boolean> {
    try {
      // Request port from user
      this.port = await navigator.serial.requestPort();
      // Open port with common baud rate for embedded devices
      await this.port.open({ 
        baudRate: 115200,
        dataBits: 8,
        stopBits: 1,
        parity: 'none',
        flowControl: 'none'
      });

      // Start reading data
      this._startReading();

      return true;
    } catch (error) {
      this.disconnect();
      throw error;
    }
  }

  // Disconnect from the port
  disconnect(): void {
    this._isReading = false;

    // Close reader
    if (this.reader) {
      this.reader.cancel().catch(() => {/* Ignore */});
      this.reader = null;
    }

    // Close writer
    if (this.writer) {
      this.writer.releaseLock();
      this.writer = null;
    }

    // Close port
    if (this.port) {
      this.port.close().catch(() => {/* Ignore */});
      this.port = null;
    }

    this._cleanup();
  }

  // Send command to the connected device
  async sendCommand(command: string): Promise<boolean> {
    if (!this.port || !this.port.writable) {
      throw new Error('Not connected to a device');
    }

    try {
      const encoder = new TextEncoder();
      const fullData = encoder.encode(command + '\r\n'); // Add CR+LF terminator
      
      // Get writer if not already available
      if (!this.writer) {
        this.writer = this.port.writable.getWriter();
      }

      // Write data
      await this.writer.write(fullData);
      
      return true;
    } catch (error) {
      // Release writer on error
      if (this.writer) {
        this.writer.releaseLock();
        this.writer = null;
      }
      
      throw error;
    }
  }

  // Start reading data from the port
  private async _startReading(): Promise<void> {
    if (!this.port || !this.port.readable) {
      return;
    }

    this._isReading = true;

    try {
      while (this.port.readable && this._isReading) {
        this.reader = this.port.readable.getReader();
        
        try {
          while (this._isReading) {
            const { value, done } = await this.reader.read();
            
            if (done) {
              break;
            }
            
            if (value) {
              this._handleReceivedData(value);
            }
          }
        } catch (_error) {
          // If it's a fatal error, trigger disconnect
          if (this.onDisconnected) {
            this.onDisconnected();
          }
          break;
        } finally {
          if (this.reader) {
            this.reader.releaseLock();
            this.reader = null;
          }
        }
      }
    } catch {
      // Ignore fatal errors
    }
  }

  // Handle incoming data from the device
  private _handleReceivedData(value: Uint8Array): void {
    // Convert received data to string and filter out null bytes
    const decoder = new TextDecoder('utf-8');
    const chunk = decoder.decode(value).replace(/\0/g, ''); // Remove null bytes

    // Accumulate chunks until newline
    this._receivedData += chunk;

    if (chunk.includes('\n')) {
      // Clean up the response: remove null bytes, trim whitespace
      const fullResponse = this._receivedData
        .replace(/\0/g, '')  // Remove any remaining null bytes
        .replace(/\r\n/g, '\n')  // Normalize line endings
        .trim();
      
      this._receivedData = ''; // Reset for next message

      if (this.onDataReceived) {
        this.onDataReceived(fullResponse);
      }
    }
  }

  // Clean up resources
  private _cleanup(): void {
    this._receivedData = '';
    this._isReading = false;
  }
}

// Export a singleton instance
export const usbSerialService = new UsbSerialService();
